'use client';
// ProSortTabs — THE expert-list sort control (M3, Founder 02/10/26: "אהבתי את הטאב שמסנן מומחים; ברירת מחדל: קרוב והכי מומלץ / הכי
// הרבה המלצות"). ONE component for every design (control cards, 2.x previews, every 3.x renderer); the sort STATE lives in
// CategoryExpertsList (one owner) which sorts the FULL list before slicing "show more".
// 'near' = the order exactly as the server/list door produced it (lib/proRankScore + same-city tier via lib/proMatching) — never
// re-sorted here; 'reviews' = reviewCount DESC, ties keep that order (stable, explicit index tie-break).
import { useRef, type CSSProperties, type KeyboardEvent } from 'react';
import { trackEvent } from '@/components/Tracker';

export type ProSortKey = 'near' | 'reviews';
export const PRO_SORTS: { k: ProSortKey; label: string }[] = [
  { k: 'near', label: 'קרוב והכי מומלץ' },
  { k: 'reviews', label: 'הכי הרבה המלצות' },
];
// ct2 chip labels (Founder 15/10): short words + icon, styled like the heading city chip
const CHIP: Record<ProSortKey, { label: string; icon: string }> = {
  near: { label: 'קרוב', icon: 'M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21zM12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z' },
  reviews: { label: 'מספר המלצות', icon: 'M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6-4.9-4.6 6.6-.8z' },
};
export const PRO_SORT_DEFAULT: ProSortKey = 'near';
export const PRO_SORT_MIN = 3; // the control shows only when there are at least this many experts

export function sortPros<T extends { reviewCount: number }>(list: T[], key: ProSortKey): T[] {
  if (key !== 'reviews') return list; // default: the received order, untouched (same array)
  return list.map((e, i) => ({ e, i }))
    .sort((a, b) => ((Number(b.e.reviewCount) || 0) - (Number(a.e.reviewCount) || 0)) || (a.i - b.i))
    .map(x => x.e);
}

export default function ProSortTabs({ value, onChange, count, categorySlug, style, chips = false }: {
  value: ProSortKey; onChange: (k: ProSortKey) => void; count: number; categorySlug?: string; style?: CSSProperties; chips?: boolean;
}) {
  const btns = useRef<(HTMLButtonElement | null)[]>([]);
  const idx = Math.max(0, PRO_SORTS.findIndex(s => s.k === value));
  const pick = (k: ProSortKey) => {
    if (k === value) return;
    trackEvent('sort_change', { metadata: { to: k, from: value, n: count }, categorySlug });
    onChange(k);
  };
  const onKey = (ev: KeyboardEvent) => {
    // RTL: ArrowLeft = next, ArrowRight = previous
    const d = ev.key === 'ArrowLeft' || ev.key === 'ArrowDown' ? 1 : ev.key === 'ArrowRight' || ev.key === 'ArrowUp' ? -1 : 0;
    if (!d) return;
    ev.preventDefault();
    const n = (idx + d + PRO_SORTS.length) % PRO_SORTS.length;
    pick(PRO_SORTS[n].k); btns.current[n]?.focus();
  };
  // Founder 15/10 (2nd+3rd note): ONE unified pill toggle — white track, the selected option is a blue knob (white text) that slides
  if (chips) return (
    <div style={{ display: 'flex', justifyContent: 'center', ...style }}>
      <div role="radiogroup" aria-label="מיון המומחים" data-pro-sort className="pst-seg"
        style={{ position: 'relative', display: 'inline-grid', gridTemplateColumns: `repeat(${PRO_SORTS.length}, 1fr)`, padding: 4, borderRadius: 999, background: '#fff', border: '1.5px solid #c9cdf0', boxShadow: '0 6px 18px rgba(10,61,143,.12)' }}>
        <span aria-hidden="true" style={{ position: 'absolute', top: 4, bottom: 4, width: `calc((100% - 8px) / ${PRO_SORTS.length})`, insetInlineStart: `calc(4px + ${idx} * (100% - 8px) / ${PRO_SORTS.length})`, background: '#0a3d8f', borderRadius: 999, boxShadow: '0 2px 6px rgba(4,1,84,.25)', transition: 'inset-inline-start .32s cubic-bezier(.3,1.3,.5,1)' }} />
        {PRO_SORTS.map((s, i) => {
          const on = s.k === value, c = CHIP[s.k];
          return (
            <button key={s.k} ref={el => { btns.current[i] = el; }} type="button" role="radio" aria-checked={on} tabIndex={on ? 0 : -1}
              data-sort-key={s.k} className="pst-chip" onClick={() => pick(s.k)} onKeyDown={onKey}
              style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, minHeight: 44, padding: '0 18px', borderRadius: 999, border: 'none', background: 'none', color: on ? '#fff' : '#0a3d8f', fontFamily: 'inherit', fontSize: 15, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'color .25s' }}>
              <svg aria-hidden="true" viewBox="0 0 24 24" width="18" height="18" style={{ display: 'block', flex: 'none' }} fill={s.k === 'reviews' ? '#FBBC04' : on ? '#fff' : '#ea4335'}><path fillRule="evenodd" d={c.icon} /></svg>
              {c.label}
            </button>
          );
        })}
      </div>
    </div>
  );
  const w = `calc((100% - 4px) / ${PRO_SORTS.length})`;
  return (
    <div role="radiogroup" aria-label="מיון המומחים" data-pro-sort
      style={{ position: 'relative', display: 'flex', height: 36, borderRadius: 10, background: '#eef2f7', padding: 2, ...style }}>
      <span aria-hidden="true" style={{ position: 'absolute', top: 2, bottom: 2, width: w, insetInlineStart: `calc(2px + ${idx} * ${w})`, background: '#fff', borderRadius: 8, boxShadow: '0 0 0 1px rgba(15,23,42,.10), 0 1px 2px rgba(15,23,42,.06)', transition: 'inset-inline-start .32s cubic-bezier(.3,1.4,.5,1)' }} />
      {PRO_SORTS.map((s, i) => {
        const on = s.k === value;
        return (
          <button key={s.k} ref={el => { btns.current[i] = el; }} type="button" role="radio" aria-checked={on} tabIndex={on ? 0 : -1}
            data-sort-key={s.k} className="pst-btn" onClick={() => pick(s.k)} onKeyDown={onKey}
            style={{ position: 'relative', flex: 1, minWidth: 0, background: 'none', border: 'none', borderRadius: 8, padding: '0 6px', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: on ? '#1d4ed8' : '#334155', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {/* invisible hit area: 32px visual + 6px above/below = 44px tall */}
            <span aria-hidden="true" style={{ position: 'absolute', inset: '-6px 0' }} />
            {s.label}
          </button>
        );
      })}
    </div>
  );
}
